import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import {
  backhaulRedundancy,
  carrierMediumText,
  priorityLabel,
  sortLegsByPriority,
} from "kontrolEdge/helpers/carrierAccess";
import { ParentConnectionLink, formatParentConnection } from "../CarrierAccessData";
import { legStatusTag, legUplinkText } from "./carrierAccessLegs";
import type { CarrierAccessLeg } from "../carrierAccess.types";

const columns: GenericTableColumn<CarrierAccessLeg>[] = [
  { header: "Carrier", accessor: "name" },
  {
    header: "Priority",
    renderCell: (leg) => <div>{priorityLabel(leg.priority) ?? "-"}</div>,
    sortAccessor: (leg) => priorityLabel(leg.priority) ?? "~",
  },
  {
    header: "Medium",
    renderCell: (leg) => <div>{carrierMediumText(leg.medium) ?? "-"}</div>,
    sortAccessor: (leg) => carrierMediumText(leg.medium) ?? "~",
  },
  {
    // Our site the carrier lands on, and the CPE router it leaves from.
    header: "Uplink",
    renderCell: (leg) => <div>{legUplinkText(leg) ?? "-"}</div>,
    sortAccessor: (leg) => legUplinkText(leg) ?? "~",
  },
  {
    header: "Parent Connection",
    renderCell: (leg) =>
      leg.endpoint ? <ParentConnectionLink endpoint={leg.endpoint} /> : "-",
    sortAccessor: (leg) =>
      leg.endpoint ? formatParentConnection(leg.endpoint) : "",
  },
  {
    header: "Status",
    renderCell: (leg) => {
      const { label, variant } = legStatusTag(leg.status);
      return (
        <ServiceStatusValue
          label={label}
          variant={variant}
          tooltip={leg.statusMessage ?? undefined}
        />
      );
    },
    sortAccessor: (leg) => legStatusTag(leg.status).label,
  },
];

/**
 * The legs of the Backhaul, primary first, with its redundancy ("Dual ·
 * 1+1") in the header. The reconcilers derive the legs from the descriptions
 * networking writes on the routers. Which carrier is active and which
 * standby is the customer's concern (Jose, PR #696); their circuit and
 * service request are ours, and stay in the Admin Center.
 *
 * Read-only for everyone, a Global Admin included: priority and medium are
 * part of what was sold and come from the database, captured in the Admin
 * Center, not here.
 */
const CarrierAccessLegsCard: React.FC<{ legs: CarrierAccessLeg[] }> = ({ legs }) => {
  const redundancy = backhaulRedundancy(legs.length);

  return (
    <GenericCard
      isCollapsible
      initialExpanded
      headerContent="Carriers"
      rightContent={
        redundancy ? (
          <span className="text-sm text-semantic-text-secondary">{redundancy}</span>
        ) : undefined
      }
      bodyContent={
        legs.length === 0 ? (
          <p className="py-1.5 text-sm text-semantic-text-secondary">
            No carriers are linked to this Backhaul yet.
          </p>
        ) : (
          <GenericTable
            columns={columns}
            data={sortLegsByPriority(legs)}
            tableClassName="w-full"
            isFooterVisible={false}
          />
        )
      }
    />
  );
};

export default CarrierAccessLegsCard;
